<template>
  <div class="content">
    <b-card class="survey-informations-card mt-3">
      <header slot="header">
        <b-row>
          <b-col :title="labels.basicInformations" md="12">
            <span class="bold">{{ labels.basicInformations }}</span>
          </b-col>
        </b-row>
      </header>
      <b-row>
        <b-col class="pb-3" cols="12" xl="6">
          <div
            v-for="(language, index) in availableLanguages"
            :key="`title_${index}`"
          >
            <b-form-group
              v-if="isLanguageSelected(index)"
              :description="labels.surveyTitle.description"
            >
              <b-form-input
                class="name-form-group custom-survey-form-input"
                :placeholder="labels.surveyTitle.placeholder"
                :value="title[language]"
                @input="changeLanguageProperty($event, 'title', language)"
              />
              <label class="custom-input-label">
                <img
                  :src="getLanguageIcon(availableLanguages[selectedLanguage])"
                  class="language-icon"
                />
                {{ labels.surveyTitle.label }}
              </label>
            </b-form-group>
          </div>
        </b-col>
        <b-col class="pb-3" cols="12" xl="6">
          <div
            v-for="(language, index) in availableLanguages"
            :key="`title_${index}`"
          >
            <b-form-group
              v-if="isLanguageSelected(index)"
              :description="labels.surveyDescription.description"
            >
              <b-form-textarea
                class="
                  survey-description name-form-group custom-survey-form-input
                "
                :placeholder="labels.surveyDescription.placeholder"
                rows="5"
                :value="description[language]"
                @input="changeLanguageProperty($event, 'description', language)"
              />
              <label class="custom-input-label">
                <img
                  :src="getLanguageIcon(availableLanguages[selectedLanguage])"
                  class="language-icon"
                />
                {{ labels.surveyDescription.label }}
              </label>
            </b-form-group>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <b-card class="survey-informations-card questions">
      <header slot="header">
        <b-row>
          <b-col md="12">
            <b-navbar class="survey-informations-navbar">
              <b-navbar-nav>
                <b-nav-item
                  class="survey-informations-navbar-item"
                  :active="isActive(0)"
                  @click="setActiveSurveyInformationsTab(0)"
                >
                  <span class="pr-3 pl-4">{{ labels.questions }}</span>
                  <b-badge pill :variant="getVariant(0)" class="mr-3">
                    {{ questions.length }}
                  </b-badge>
                </b-nav-item>
                <b-nav-item
                  class="survey-informations-navbar-item"
                  :active="isActive(1)"
                  @click="setActiveSurveyInformationsTab(1)"
                >
                  <span class="pr-3 pl-4">{{ labels.categories }}</span>
                  <b-badge pill :variant="getVariant(1)" class="mr-3">
                    {{ categories.length }}
                  </b-badge>
                </b-nav-item>
              </b-navbar-nav>
            </b-navbar>
          </b-col>
        </b-row>
      </header>
      <QuestionsConfiguration
        v-if="activeSurveyInformationsTab === 0"
        :questions="questions"
        :categories="categories"
        :availableLanguages="availableLanguages"
        :selectedLanguage="selectedLanguage"
        @addQuestion="$emit('addQuestion')"
        @removeQuestion="removeQuestion"
        @changeLanguageListProperty="changeLanguageListProperty"
        @changeListProperty="changeListProperty"
        @moveQuestion="moveQuestion"
        @createCategoryOfQuestion="createCategoryOfQuestion"
        @addAnswer="addAnswer"
        @removeAnswer="removeAnswer"
        @togglePopoverAddCategory="togglePopoverAddCategory"
        @changeLanguageListOfListProperty="changeLanguageListOfListProperty"
        @changeListOfListProperty="changeListOfListProperty"
        @moveUpQuestionAnswer="moveUpQuestionAnswer"
        @moveDownQuestionAnswer="moveDownQuestionAnswer"
      />
      <CategoriesConfiguration
        v-if="activeSurveyInformationsTab === 1"
        :categories="categories"
        :questions="questions"
        :availableLanguages="availableLanguages"
        :selectedLanguage="selectedLanguage"
        @addCategory="$emit('addCategory')"
        @removeCategory="removeCategory"
        @changeLanguageListProperty="changeLanguageListProperty"
        @changeListProperty="changeListProperty"
        @moveCategory="moveCategory"
        @removeQuestionFromCategory="removeQuestionFromCategory"
      />
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import QuestionsConfiguration from '../QuestionsConfiguration.vue'
import CategoriesConfiguration from '../CategoriesConfiguration.vue'

const props = {
  questions: {
    type: Array,
  },
  availableLanguages: {
    type: Array,
  },
  languages: {
    type: Array,
  },
  selectedLanguage: {
    type: Number,
  },
  title: {
    type: Object,
  },
  description: {
    type: Object,
  },
  categories: {
    type: Array,
  },
}

export default {
  props,
  components: {
    QuestionsConfiguration,
    CategoriesConfiguration,
  },
  data() {
    return {
      activeSurveyInformationsTab: 0,
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
    }),
    labels() {
      return this.resources.component.customSurveys.create.tabs
    },
  },
  methods: {
    getLanguageIcon(language) {
      return `/icons/languages/${language}.svg`
    },
    isLanguageSelected(language) {
      return this.selectedLanguage === language
    },
    changeLanguageProperty(value, property, language) {
      this.$emit('changeLanguageProperty', { property, language, value })
    },
    isActive(tab) {
      return this.activeSurveyInformationsTab === tab
    },
    setActiveSurveyInformationsTab(tab) {
      this.activeSurveyInformationsTab = tab
    },
    getVariant(tab) {
      return this.isActive(tab) ? 'secondary' : 'white'
    },
    removeQuestion(idx) {
      this.$emit('removeQuestion', idx)
    },
    changeLanguageListProperty({
      value,
      collection,
      index,
      property,
      language,
    }) {
      this.$emit('changeLanguageListProperty', {
        value,
        collection,
        index,
        property,
        language,
      })
    },
    changeListProperty({ value, collection, index, property }) {
      this.$emit('changeListProperty', {
        value,
        collection,
        index,
        property,
      })
    },
    removeCategory(idx) {
      this.$emit('removeCategory', idx)
    },
    moveQuestion({ from, to }) {
      this.$emit('moveQuestion', { from, to })
    },
    moveCategory({ from, to }) {
      this.$emit('moveCategory', { from, to })
    },
    createCategoryOfQuestion({ questionIndex, categoryLabel }) {
      this.$emit('createCategoryOfQuestion', { questionIndex, categoryLabel })
    },
    addAnswer(questionIndex) {
      this.$emit('addAnswer', { questionIndex })
    },
    removeAnswer({ answerId, questionIndex }) {
      this.$emit('removeAnswer', { answerId, questionIndex })
    },
    togglePopoverAddCategory({ index, value }) {
      this.$emit('togglePopoverAddCategory', { index, value })
    },
    changeLanguageListOfListProperty({
      value,
      collection,
      index,
      listCollection,
      listIndex,
      property,
      language,
    }) {
      this.$emit('changeLanguageListOfListProperty', {
        value,
        collection,
        index,
        listCollection,
        listIndex,
        property,
        language,
      })
    },
    changeListOfListProperty({
      value,
      collection,
      index,
      listCollection,
      listIndex,
      property,
    }) {
      this.$emit('changeListOfListProperty', {
        value,
        collection,
        index,
        listCollection,
        listIndex,
        property,
      })
    },
    removeQuestionFromCategory(categoryId, questionId) {
      this.$emit('removeQuestionFromCategory', { categoryId, questionId })
    },
    moveUpQuestionAnswer({ qIdx, index }) {
      this.$emit('moveUpQuestionAnswer', { qIdx, index })
    },
    moveDownQuestionAnswer({ qIdx, index }) {
      this.$emit('moveDownQuestionAnswer', { qIdx, index })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.survey-informations-card {
  &.questions {
    margin-top: 30px;
    border: 0 !important;

    .card-header {
      padding: 0;
      overflow: hidden !important;
      border: 1px solid var(--gray07) !important;
    }
    .card-body {
      padding: 0;
      background-color: map-get($theme-colors, 'white02');
    }
  }

  .language-icon {
    width: 15px;
    height: 15px;
  }

  .survey-description {
    height: auto !important;
    background-color: map-get($theme-colors, 'gray08') !important;
    border-color: map-get($theme-colors, 'purple05') !important;
  }

  .survey-informations-navbar {
    padding-top: 0;
    padding-left: 0;
    font-size: 14pt;

    .survey-informations-navbar-item {
      padding-top: 7px;
      border-right: 1px solid map-get($theme-colors, 'gray07');

      &:last-child {
        border-right: unset;
      }
    }

    .active {
      border-bottom: 5px solid map-get($theme-colors, 'secondary');
      color: map-get($theme-colors, 'secondary') !important;
      font-weight: bold;
    }

    .badge-white {
      border: 1px solid var(--gray07) !important;
    }
  }
}
</style>
